import { FormLabel, GovFormWeb } from '@gov-design-system-ce/react-form';
import { useLocale } from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useVlastniDokladyContext } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	Dialog,
	FormDatePicker,
	FormFileUpload,
	FormInput,
	FormTextarea,
	FormWidthElementLayout,
	Icon,
	LayoutGap,
} from '@gov-nx/ui/web';
import { MarkdownRender } from '../../../Markdown/MarkdownRender';
import { OwnDocumentFormSkeleton } from './OwnDocumentFormSkeleton';

interface EditOwnDocumentFormProps {
	code: PageCode;
}

export const OwnDocumentForm = ({ code }: EditOwnDocumentFormProps) => {
	const {
		documentId,
		controls,
		isEditAction,
		formDefinition,
		onSubmit,
		fileUploadHook,
		soubory,
		downloadedFile,
		downloadImage,
		onDeleteImage,
		fileToDelete,
		setFileToDelete,
	} = useVlastniDokladyContext();
	const { t } = useLocale(code);

	if (controls.initialLoading) {
		return <OwnDocumentFormSkeleton />;
	}

	return (
		<ContentLayout>
			<GovFormWeb
				formDefinition={formDefinition}
				onSubmit={onSubmit}>
				<LayoutGap gap="l">
					<FormWidthElementLayout type="l">
						<FormInput
							field={{
								name: 'nazev',
								isDisabled: controls.processLoading,
								placeholder: t('formular.placeholder.nazev'),
							}}
							label={{
								children: t('formular.pole.nazev'),
							}}
						/>
					</FormWidthElementLayout>

					<div className="d-flex flex-column gap-2">
						{!!soubory?.length && (
							<>
								<FormLabel>{t('formular.pole.nahrane-fotografie')}</FormLabel>

								{soubory.map((soubor) => (
									<div
										className="flex items-center justify-between gap-2"
										key={soubor.souborId}>
										<Button
											color="primary"
											type="link"
											isDisabled={downloadImage.mutation.isLoading}
											isLoading={downloadedFile === soubor.souborId}
											onClick={() => {
												if (documentId && soubor.souborId) {
													downloadImage.fetchFullFile({
														documentId,
														souborId: soubor.souborId,
														onSuccess: (blob) => {
															window.open(URL.createObjectURL(blob));
														},
													});
												}
											}}>
											<span className="truncate max-w-[190px] md:max-w-[600px]">
												{soubor.nazev}
											</span>
										</Button>
										<Button
											color="error"
											type="base"
											size="m"
											isDisabled={downloadImage.mutation.isLoading}
											onClick={() => setFileToDelete(soubor.souborId ?? null)}
											aria-label={t('formular.akce.odstranit')}
											iconStart={
												<Icon
													name="trash"
													type="templates"
												/>
											}>
											{t('formular.akce.odstranit')}
										</Button>
									</div>
								))}
							</>
						)}
						<Dialog
							isOpen={!!fileToDelete}
							onClose={() => setFileToDelete(null)}
							title={<h3>{t('formular.potvrdit-odstraneni.title')}</h3>}
							footer={
								<>
									<Button
										type="base"
										color="primary"
										size="m"
										onClick={() => setFileToDelete(null)}
										aria-label={t('formular.akce.zrusit')}>
										{t('formular.akce.zrusit')}
									</Button>
									<Button
										type="solid"
										color="error"
										size="m"
										onClick={() => {
											if (documentId && fileToDelete) {
												onDeleteImage(documentId, fileToDelete);
												setFileToDelete(null);
											}
										}}
										aria-label={t('formular.akce.odstranit')}>
										{t('formular.akce.odstranit')}
									</Button>
								</>
							}>
							<p>{t('formular.potvrdit-odstraneni.description')}</p>
						</Dialog>
					</div>

					<FormFileUpload
						fileUploadHook={fileUploadHook}
						field={{ name: 'soubor', expanded: true }}
						label={{ children: t('formular.pole.pridat-fotografie') }}
					/>

					<FormWidthElementLayout type="l">
						<LayoutGap gap="m">
							<FormInput
								field={{
									name: 'cisloDokumentu',
									isDisabled: controls.processLoading,
									placeholder: t('formular.placeholder.cislo-dokumentu'),
								}}
								label={{
									children: t('formular.pole.cislo-dokumentu'),
								}}
							/>

							<FormDatePicker
								field={{
									name: 'platnostOd',
									isDisabled: controls.processLoading,
								}}
								label={{
									children: t('formular.pole.platnost-od'),
								}}
							/>
							<FormDatePicker
								field={{
									name: 'platnostDo',
									isDisabled: controls.processLoading,
								}}
								label={{
									children: (
										<MarkdownRender>
											{t('formular.pole.platnost-do')}
										</MarkdownRender>
									),
								}}
							/>
						</LayoutGap>
					</FormWidthElementLayout>
					<FormTextarea
						field={{
							name: 'komentar',
							isDisabled: controls.processLoading,
							placeholder: t('formular.placeholder.komentar'),
							rows: 4,
						}}
						label={{
							children: t('formular.pole.komentar'),
						}}
					/>

					<ButtonGroupLayout>
						<Button
							nativeType="submit"
							aria-label={
								isEditAction
									? t('formular.akce.ulozit')
									: t('formular.akce.pridat')
							}
							isDisabled={
								controls.processLoading || fileUploadHook.hasNotFinishedUploads
							}
							color="primary"
							size="m"
							type="solid"
							isExpandedMobile>
							{isEditAction
								? t('formular.akce.ulozit')
								: t('formular.akce.pridat')}
						</Button>
					</ButtonGroupLayout>
				</LayoutGap>
			</GovFormWeb>
		</ContentLayout>
	);
};
